<script setup>
// NOOVI: resumo de desempenho do robô/IA na Overview — mesma fonte que
// /reports/bot já usa (reports#bot_summary → V2::Reports::Conversations::
// MetricBuilder#bot_summary), com fetchBotSummary/getBotSummary já prontos
// no Vuex (store/modules/reports.js). Só faltava um consumidor na Overview.
// Condicional a `captain` estar habilitado na conta — mesmo padrão de
// PipelineSummaryContainer.vue com `pipeline_board`.
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { STATUS } from 'dashboard/store/constants';
import MetricCard from 'dashboard/components-next/Reports/MetricCard.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);
const { calculateTrend, displayMetric, fetchingStatus } = useReportMetrics(
  'getBotSummary',
  'getBotSummaryFetchingStatus'
);
const botSummary = useMapGetter('getBotSummary');

// Ao contrário das métricas de conversas (useReportMetrics.isAverageMetricType
// só sabe distinguir tempo médio de contagem), aqui as DUAS chaves são
// contagens — mas subir não significa "bom" pras duas: mais resoluções pelo
// robô é bom, mais transferências pra humano (handoff) é o robô resolvendo
// MENOS sozinho. Regra específica, não a genérica de useReportMetrics.
const METRICS = [
  {
    key: 'bot_resolutions_count',
    nameKey: 'REPORT.METRICS.BOT_RESOLUTION_COUNT.NAME',
    icon: 'i-lucide-bot',
    color: 'teal',
    increaseIsGood: true,
  },
  {
    key: 'bot_handoffs_count',
    nameKey: 'REPORT.METRICS.BOT_HANDOFF_COUNT.NAME',
    icon: 'i-lucide-user-round',
    color: 'amber',
    increaseIsGood: false,
  },
];

const cards = computed(() =>
  METRICS.map(metric => {
    const trend = calculateTrend(metric.key);
    return {
      ...metric,
      title: t(metric.nameKey),
      value: displayMetric(metric.key),
      trendLabel: `${Math.abs(trend)}%`,
      trendDirection: trend >= 0 ? 'up' : 'down',
      trendPositive: metric.increaseIsGood ? trend >= 0 : trend <= 0,
    };
  })
);

const isLoading = computed(() => fetchingStatus.value === STATUS.FETCHING);

// Taxas do mockup (barras de progresso "resolvido pelo robô vs. transferido
// pra humano") — proporção entre as duas contagens já mostradas nos cards
// acima, não uma métrica nova. Guarda contra total=0 (sem atividade do robô
// no período: nenhuma barra tem sentido, então some com as duas em vez de
// mostrar 0%/0% preenchido a zero, que parece "sem transferência" errado).
const botActivityTotal = computed(
  () =>
    (botSummary.value.bot_resolutions_count || 0) +
    (botSummary.value.bot_handoffs_count || 0)
);
const hasBotActivity = computed(() => botActivityTotal.value > 0);
const resolutionRate = computed(() =>
  hasBotActivity.value
    ? Math.round(
        ((botSummary.value.bot_resolutions_count || 0) * 100) /
          botActivityTotal.value
      )
    : 0
);
const handoffRate = computed(() =>
  hasBotActivity.value ? 100 - resolutionRate.value : 0
);

const fetchBotSummary = () => {
  store.dispatch('fetchBotSummary', {
    from: dateFilter.from,
    to: dateFilter.to,
    businessHours: dateFilter.businessHours,
  });
};

onMounted(fetchBotSummary);
watch(
  () => [dateFilter.from, dateFilter.to, dateFilter.businessHours],
  fetchBotSummary
);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.BOT_SUMMARY.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.BOT_SUMMARY.HEADER') }}
        </h5>
      </div>
    </template>
    <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
      <MetricCard
        v-for="card in cards"
        :key="card.key"
        :title="card.title"
        :value="card.value"
        :icon="card.icon"
        :color="card.color"
        :trend-label="card.trendLabel"
        :trend-direction="card.trendDirection"
        :trend-positive="card.trendPositive"
      />
    </div>
    <div v-if="hasBotActivity" class="flex flex-col gap-3 mt-4">
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-slate-11">
            {{ t('OVERVIEW_REPORTS.BOT_SUMMARY.RESOLUTION_RATE') }}
          </span>
          <span class="font-medium text-n-slate-12">{{ resolutionRate }}%</span>
        </div>
        <ProgressBar
          :value="resolutionRate"
          color="teal"
          :label="t('OVERVIEW_REPORTS.BOT_SUMMARY.RESOLUTION_RATE')"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-slate-11">
            {{ t('OVERVIEW_REPORTS.BOT_SUMMARY.HANDOFF_RATE') }}
          </span>
          <span class="font-medium text-n-slate-12">{{ handoffRate }}%</span>
        </div>
        <ProgressBar
          :value="handoffRate"
          color="amber"
          :label="t('OVERVIEW_REPORTS.BOT_SUMMARY.HANDOFF_RATE')"
        />
      </div>
    </div>
  </OverviewCard>
</template>
